import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminSettings() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    zelle_handle: "",
    cashapp_handle: "",
    venmo_handle: "",
    free_shipping_threshold: "",
    shipping_cost: ""
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }

    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings({
          zelle_handle: response.data.zelle_handle,
          cashapp_handle: response.data.cashapp_handle,
          venmo_handle: response.data.venmo_handle,
          free_shipping_threshold: response.data.free_shipping_threshold.toString(),
          shipping_cost: response.data.shipping_cost.toString()
        });
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings`,
        {
          ...settings,
          free_shipping_threshold: parseFloat(settings.free_shipping_threshold),
          shipping_cost: parseFloat(settings.shipping_cost)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!user?.is_admin) return null;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-4 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="admin-settings">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span className="label-text">Back to Dashboard</span>
          </Link>
          <h1 className="text-4xl heading text-[#EDEDED]">Store Settings</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Payment Handles */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-2">Payment Information</h3>
            <p className="text-[#888] text-sm mb-6">
              These handles will be shown to customers at checkout
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="zelle" className="text-[#888] label-text flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#6D1ED4] flex items-center justify-center text-white text-xs font-bold">Z</div>
                  Zelle Email/Phone
                </Label>
                <Input
                  id="zelle"
                  value={settings.zelle_handle}
                  onChange={(e) => setSettings(prev => ({ ...prev, zelle_handle: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="gripcityracing@gmail.com"
                  data-testid="zelle-handle"
                />
              </div>

              <div>
                <Label htmlFor="cashapp" className="text-[#888] label-text flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#00D632] flex items-center justify-center text-white text-xs font-bold">$</div>
                  Cash App
                </Label>
                <Input
                  id="cashapp"
                  value={settings.cashapp_handle}
                  onChange={(e) => setSettings(prev => ({ ...prev, cashapp_handle: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="$GripCityRacing"
                  data-testid="cashapp-handle"
                />
              </div>

              <div>
                <Label htmlFor="venmo" className="text-[#888] label-text flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#008CFF] flex items-center justify-center text-white text-xs font-bold">V</div>
                  Venmo
                </Label>
                <Input
                  id="venmo"
                  value={settings.venmo_handle}
                  onChange={(e) => setSettings(prev => ({ ...prev, venmo_handle: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="@GripCityRacing"
                  data-testid="venmo-handle"
                />
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-2">Shipping</h3>
            <p className="text-[#888] text-sm mb-6">
              Configure shipping costs and free shipping threshold
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shipping_cost" className="text-[#888] label-text">Shipping Cost ($)</Label>
                <Input
                  id="shipping_cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.shipping_cost}
                  onChange={(e) => setSettings(prev => ({ ...prev, shipping_cost: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="9.99"
                  data-testid="shipping-cost"
                />
              </div>

              <div>
                <Label htmlFor="free_shipping" className="text-[#888] label-text">Free Shipping Over ($)</Label>
                <Input
                  id="free_shipping"
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.free_shipping_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, free_shipping_threshold: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="100.00"
                  data-testid="free-shipping-threshold"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            className="w-full btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            <Save size={20} className="transform skewX-[12deg]" />
            <span>{saving ? "Saving..." : "Save Settings"}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
